import { AbstractVpaDataTableReport } from '../../report/abstract-vpa-data-table-report';
import ActiveFiltersInterface from '@vertical-plus/vue-js-components/lib/model/vos/search/active-filters-interface';
import ColumnInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/column-interface';
import FilterInterface from '@vertical-plus/vue-js-components/lib/model/vos/search/filter-interface';
import JobSearchConditionsInterface from '@/model/vo/job/job-search-conditions-interface';
import JobSearchResultsVo from '@/model/vo/job/job-search-results-vo';
import ServiceContainer from '../../service-container';
import SortByInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/sort-by-interface';
import Techspec from '@/model/entity/job/techspec';
import WidgetInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/widget-interface';

export default class JobDataTableReport extends AbstractVpaDataTableReport
{
    public id = 'jobs';

    public get widgets(): WidgetInterface[]
    {
        return [];
    }

    public get filters(): FilterInterface[]
    {
        // Don't use filters for meetings as we want to see all jobs
        if (this.techspec?.meetingDate)
        {
            return [];
        }

        return [
            {
                key: 'id',
                type: 'string',
                title: this.i18n('general.field.id'),
            },
            {
                key: 'name',
                type: 'string',
                title: this.i18n('general.field.name'),
            },
            {
                key: 'client',
                type: 'string',
                title: this.i18n('general.field.client'),
                optionsCallback: async(search: string) => this.serviceContainer.clientService.clientFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.clientService.clientFormTitlesCallback(values),
            },
            {
                key: 'techspec',
                type: 'string',
                title: this.i18n('general.field.techspec'),
                optionsCallback: async(search: string) => this.serviceContainer.techspecService.techspecFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.techspecService.techspecFormTitlesCallback(values),
            },
            {
                key: 'creator',
                type: 'string',
                title: this.i18n('job.field.assigned_by'),
                optionsCallback: async(search: string) => this.serviceContainer.userService.formOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.userService.formTitlesCallback(values),
            },
            {
                key: 'assignee',
                type: 'string',
                title: this.i18n('job.field.assigned_to'),
                optionsCallback: async(search: string) => this.serviceContainer.userService.formOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.userService.formTitlesCallback(values),
            },
            {
                key: 'job_owner',
                type: 'string',
                title: this.i18n('job.field.job_owner'),
                optionsCallback: async(search: string) => this.serviceContainer.userService.formOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.userService.formTitlesCallback(values),
            },
            {
                key: 'techspec.manager',
                type: 'string',
                title: this.i18n('job.field.techspec_owner'),
                optionsCallback: async(search: string) => this.serviceContainer.userService.formOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.userService.formTitlesCallback(values),
            },
            {
                key: 'stage',
                type: 'string',
                title: this.i18n('job.field.stage'),
                optionsCallback: async(search: string) => this.serviceContainer.jobService.jobStageFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.jobService.jobStageFormTitlesCallback(values),
            },
            {
                key: 'tags.title',
                type: 'string',
                title: this.i18n('general.field.tags'),
                optionsCallback: async(search: string) => this.serviceContainer.tagService.tagFormOptionsCallback(search),
                titlesCallback: async(values: string[]) => this.serviceContainer.tagService.tagFormTitlesCallback(values),
            },
            {
                key: 'ticket',
                type: 'boolean',
                title: this.i18n('job.field.ticket'),
            },
            {
                key: 'complete',
                type: 'boolean',
                title: this.i18n('job.field.complete'),
            },
        ];
    }

    public get defaultFilters(): ActiveFiltersInterface
    {
        return {
            complete: false,
        };
    }

    public get columns(): ColumnInterface[]
    {
        return [
            {
                key: 'id',
                title: this.i18n('general.field.id'),
                width: 100,
            },
            {
                key: 'name',
                title: this.i18n('general.field.name'),
            },
            {
                key: 'type',
                title: this.i18n('general.field.type'),
                width: 150,
            },
            {
                key: 'stage.name',
                title: this.i18n('job.field.stage'),
                width: 175,
            },
            {
                key: 'creator',
                title: this.i18n('job.field.assigned_by'),
                width: 150,
            },
            {
                key: 'assignee',
                title: this.i18n('job.field.assigned_to'),
                width: 150,
            },
            {
                key: 'dateCreated',
                title: this.i18n('general.field.date_created'),
                width: 150,
            },
            {
                key: 'options',
                title: this.i18n('general.field.options'),
                width: 100,
                align: 'start',
            },
        ];
    }

    public get defaultColumns(): string[]
    {
        if (this.savedDefaultColumns)
        {
            return this.savedDefaultColumns;
        }

        return [
            'id',
            'name',
            'type',
            'stage.name',
            'creator',
            'assignee',
            'dateCreated',
            'options',
        ];
    }

    public get sortBys(): SortByInterface[]
    {
        return [
            {
                key: 'id',
                title: this.i18n('general.field.id'),
                order:
                [
                    {
                        key: 'id',
                        descending: false,
                    },
                ],
            },
            {
                key: 'name',
                title: this.i18n('general.field.name'),
                order:
                [
                    {
                        key: 'name',
                        descending: false,
                    },
                ],
            },
            {
                key: 'dateCreated',
                title: this.i18n('general.field.date_created'),
                order:
                [
                    {
                        key: 'dateCreated',
                        descending: true,
                    },
                ],
            },
        ];
    }

    public defaultSortBy = 'dateCreated';

    private techspec: Techspec|null = null;
    private results: JobSearchResultsVo|null = null;

    public constructor(serviceContainer: ServiceContainer, techspec: Techspec|null = null)
    {
        super(serviceContainer);

        this.techspec = techspec;
    }

    public setTechspec(techspec: Techspec|null)
    {
        this.techspec = techspec;
    }

    public async loadResults(conditions: JobSearchConditionsInterface): Promise<JobSearchResultsVo>
    {
        // Force techspec filter if a techspec has been defined
        if (this.techspec?.id)
        {
            conditions.techspec = this.techspec.id;
        }

        const results = await this.serviceContainer.jobService.findJobs(conditions);

        this.results = Object.assign({}, results);

        return results;
    }
}